// Advanced TanStack table for one side of the insider board (Purchases or
// Sales). Features: multi-column sorting, global text filter, column
// visibility toggles, and expandable rows that reveal the individual Form-4
// trades (with SEC filing links) -- the drill-down rows ship inside the board
// payload, so expanding is zero-network.

import { Fragment, useMemo, useState } from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    type ExpandedState,
    type SortingState,
    type VisibilityState,
} from '@tanstack/react-table';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    BarChart3,
    ChevronDown,
    ChevronRight,
    Columns3,
    ExternalLink,
    Search,
} from 'lucide-react';

import type { TopLiquidInsiderRow } from '../api/insiderTradingApi';
import {
    cn,
    formatCompact,
    formatCompactUsd,
    formatDate,
    formatPct,
    formatPrice,
    formatSignedPct,
    stockInfoRoute,
} from '../utils/formatters';

interface InsiderTradesTableProps {
    title: string;
    subtitle?: string;
    rows: TopLiquidInsiderRow[];
    theme: 'buy' | 'sell';
    isLoading?: boolean;
    selectedSymbol?: string | null;
    onSelectSymbol?: (symbol: string) => void;
}

const columnHelper = createColumnHelper<TopLiquidInsiderRow>();

const COLUMN_LABELS: Record<string, string> = {
    latest_filing_date: 'Filing Date',
    latest_transaction_date: 'Trade Date',
    insider_count: 'Insiders',
    total_shares: 'Shares',
    total_value: 'Value',
    vwap: 'Insider VWAP',
    last_price: 'Last Price',
    price_vs_vwap_pct: 'Δ vs VWAP',
    dollar_volume: 'Avg $ Volume',
    value_to_adv_pct: 'Value / ADV',
    top_insiders: 'Top Insider',
};

const SortIcon = ({ dir }: { dir: false | 'asc' | 'desc' }) =>
    dir === 'asc' ? (
        <ArrowUp className="w-3 h-3" />
    ) : dir === 'desc' ? (
        <ArrowDown className="w-3 h-3" />
    ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40" />
    );

export const InsiderTradesTable = ({
    title,
    subtitle,
    rows,
    theme,
    isLoading = false,
    selectedSymbol,
    onSelectSymbol,
}: InsiderTradesTableProps) => {
    const isBuy = theme === 'buy';
    const [sorting, setSorting] = useState<SortingState>([{ id: 'rank', desc: false }]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
        latest_transaction_date: false,
        vwap: false,
    });
    const [showColumnMenu, setShowColumnMenu] = useState(false);

    const accent = isBuy
        ? {
              border: 'border-emerald-200 dark:border-emerald-800',
              header: 'bg-emerald-50 dark:bg-emerald-900/10',
              text: 'text-emerald-700 dark:text-emerald-400',
              bar: 'bg-emerald-500',
              ring: 'ring-emerald-400',
          }
        : {
              border: 'border-rose-200 dark:border-rose-800',
              header: 'bg-rose-50 dark:bg-rose-900/10',
              text: 'text-rose-700 dark:text-rose-400',
              bar: 'bg-rose-500',
              ring: 'ring-rose-400',
          };

    const maxConviction = useMemo(
        () => Math.max(0, ...rows.map((r) => r.value_to_adv_pct ?? 0)),
        [rows],
    );

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'expander',
                enableHiding: false,
                header: () => null,
                cell: ({ row }) => (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            row.toggleExpanded();
                        }}
                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                        aria-label={row.getIsExpanded() ? 'Collapse trades' : 'Expand trades'}
                    >
                        {row.getIsExpanded() ? (
                            <ChevronDown className="w-4 h-4" />
                        ) : (
                            <ChevronRight className="w-4 h-4" />
                        )}
                    </button>
                ),
            }),
            columnHelper.accessor('rank', {
                header: '#',
                enableHiding: false,
                cell: (info) => (
                    <span className="text-xs font-bold text-slate-400">{info.getValue()}</span>
                ),
            }),
            columnHelper.accessor('symbol', {
                header: 'Symbol',
                enableHiding: false,
                cell: (info) => {
                    const r = info.row.original;
                    return (
                        <div className="flex flex-col min-w-[140px]">
                            <div className="flex items-center gap-2">
                                <a
                                    href={stockInfoRoute(r.symbol)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-bold text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    {r.symbol}
                                </a>
                                {r.exchange && (
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5">
                                        {r.exchange}
                                    </span>
                                )}
                            </div>
                            {r.name && (
                                <span className="text-xs text-slate-500 truncate max-w-[200px]">
                                    {r.name}
                                </span>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('latest_filing_date', {
                header: 'Filing Date',
                cell: (info) => (
                    <span className="font-mono text-xs">{formatDate(info.getValue())}</span>
                ),
            }),
            columnHelper.accessor('latest_transaction_date', {
                header: 'Trade Date',
                cell: (info) => (
                    <span className="font-mono text-xs">{formatDate(info.getValue())}</span>
                ),
            }),
            columnHelper.accessor('insider_count', {
                header: 'Insiders',
                cell: (info) => (
                    <div className="text-right font-mono">
                        {info.getValue()}
                        <span className="text-slate-400 text-[10px]">
                            {' '}
                            / {info.row.original.trade_count} tx
                        </span>
                    </div>
                ),
            }),
            columnHelper.accessor('total_shares', {
                header: 'Shares',
                cell: (info) => (
                    <div className="text-right font-mono">{formatCompact(info.getValue())}</div>
                ),
            }),
            columnHelper.accessor('total_value', {
                header: 'Value',
                cell: (info) => (
                    <div className={cn('text-right font-mono font-bold', accent.text)}>
                        {formatCompactUsd(info.getValue())}
                    </div>
                ),
            }),
            columnHelper.accessor('vwap', {
                header: 'Insider VWAP',
                cell: (info) => (
                    <div className="text-right font-mono">{formatPrice(info.getValue())}</div>
                ),
            }),
            columnHelper.accessor('last_price', {
                header: 'Last Price',
                cell: (info) => (
                    <div className="text-right font-mono">{formatPrice(info.getValue())}</div>
                ),
            }),
            columnHelper.accessor('price_vs_vwap_pct', {
                header: 'Δ vs VWAP',
                cell: (info) => {
                    const v = info.getValue();
                    return (
                        <div
                            className={cn(
                                'text-right font-mono font-semibold',
                                v === null
                                    ? 'text-slate-400'
                                    : v >= 0
                                      ? 'text-emerald-600 dark:text-emerald-400'
                                      : 'text-rose-600 dark:text-rose-400',
                            )}
                        >
                            {formatSignedPct(v)}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('dollar_volume', {
                header: 'Avg $ Volume',
                cell: (info) => (
                    <div className="text-right font-mono">{formatCompactUsd(info.getValue())}</div>
                ),
            }),
            columnHelper.accessor('value_to_adv_pct', {
                header: 'Value / ADV',
                cell: (info) => {
                    const v = info.getValue();
                    const width =
                        v !== null && maxConviction > 0
                            ? Math.max(4, Math.min(100, (v / maxConviction) * 100))
                            : 0;
                    return (
                        <div className="flex items-center gap-2 justify-end min-w-[110px]">
                            <div className="w-14 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                <div
                                    className={cn('h-full rounded-full', accent.bar)}
                                    style={{ width: `${width}%` }}
                                />
                            </div>
                            <span className="font-mono text-xs w-14 text-right">
                                {formatPct(v, v !== null && v < 0.1 ? 3 : 2)}
                            </span>
                        </div>
                    );
                },
            }),
            columnHelper.accessor((r) => r.top_insiders[0] ?? '', {
                id: 'top_insiders',
                header: 'Top Insider',
                enableSorting: false,
                cell: (info) => {
                    const names = info.row.original.top_insiders;
                    return (
                        <span
                            className="text-xs text-slate-600 dark:text-slate-300 truncate max-w-[180px] inline-block"
                            title={names.join(', ')}
                        >
                            {names[0] ?? '—'}
                            {names.length > 1 && (
                                <span className="text-slate-400"> +{names.length - 1}</span>
                            )}
                        </span>
                    );
                },
            }),
            columnHelper.display({
                id: 'actions',
                enableHiding: false,
                header: () => null,
                cell: ({ row }) => (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelectSymbol?.(row.original.symbol);
                        }}
                        className="p-1.5 rounded-md text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                        title="Show insider statistics"
                        aria-label={`Show insider statistics for ${row.original.symbol}`}
                    >
                        <BarChart3 className="w-4 h-4" />
                    </button>
                ),
            }),
        ],
        [accent.bar, accent.text, maxConviction, onSelectSymbol],
    );

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting, globalFilter, expanded, columnVisibility },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onExpandedChange: setExpanded,
        onColumnVisibilityChange: setColumnVisibility,
        getRowCanExpand: (row) => row.original.trades.length > 0,
        globalFilterFn: (row, _columnId, value: string) => {
            const q = value.trim().toLowerCase();
            if (!q) return true;
            const r = row.original;
            return (
                r.symbol.toLowerCase().includes(q) ||
                (r.name ?? '').toLowerCase().includes(q) ||
                r.top_insiders.some((n) => n.toLowerCase().includes(q))
            );
        },
        getRowId: (r) => `${r.transaction_type}:${r.symbol}`,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
    });

    const visibleColumnCount = table.getVisibleLeafColumns().length;

    return (
        <div
            className={cn(
                'flex flex-col rounded-xl border bg-white dark:bg-slate-900 shadow-sm overflow-hidden',
                accent.border,
            )}
        >
            {/* Header + toolbar */}
            <div
                className={cn(
                    'px-5 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-3',
                    accent.border,
                    accent.header,
                )}
            >
                <div>
                    <h3 className={cn('text-lg font-black tracking-tight', accent.text)}>{title}</h3>
                    {subtitle && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder="Filter symbol / insider…"
                            className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 w-48"
                        />
                    </div>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setShowColumnMenu((s) => !s)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                            <Columns3 className="w-3.5 h-3.5" /> Columns
                        </button>
                        {showColumnMenu && (
                            <div className="absolute right-0 mt-1 z-20 w-48 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg p-2 space-y-1">
                                {table
                                    .getAllLeafColumns()
                                    .filter((c) => c.getCanHide())
                                    .map((c) => (
                                        <label
                                            key={c.id}
                                            className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={c.getIsVisible()}
                                                onChange={c.getToggleVisibilityHandler()}
                                            />
                                            {COLUMN_LABELS[c.id] ?? c.id}
                                        </label>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-200 dark:border-slate-700">
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id}>
                                {hg.headers.map((header) => {
                                    const canSort = header.column.getCanSort();
                                    return (
                                        <th key={header.id} className="px-3 py-3">
                                            {header.isPlaceholder ? null : canSort ? (
                                                <button
                                                    type="button"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className="inline-flex items-center gap-1 uppercase hover:text-slate-800 dark:hover:text-slate-200"
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext(),
                                                    )}
                                                    <SortIcon dir={header.column.getIsSorted()} />
                                                </button>
                                            ) : (
                                                flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                        {isLoading && rows.length === 0 ? (
                            Array.from({ length: 8 }).map((_, i) => (
                                <tr key={`sk-${i}`}>
                                    <td colSpan={visibleColumnCount} className="px-3 py-3">
                                        <div className="h-5 rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
                                    </td>
                                </tr>
                            ))
                        ) : table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={visibleColumnCount}
                                    className="px-3 py-12 text-center text-sm text-slate-500"
                                >
                                    {rows.length === 0
                                        ? 'No liquid names with insider activity in this window.'
                                        : 'No rows match your filter.'}
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map((row) => (
                                <Fragment key={row.id}>
                                    <tr
                                        onClick={() => row.getCanExpand() && row.toggleExpanded()}
                                        className={cn(
                                            'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors',
                                            selectedSymbol === row.original.symbol &&
                                                cn('ring-2 ring-inset', accent.ring),
                                        )}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="px-3 py-3">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                    {row.getIsExpanded() && (
                                        <tr className="bg-slate-50/70 dark:bg-slate-800/30">
                                            <td colSpan={visibleColumnCount} className="px-4 py-3">
                                                <TradeDrilldown row={row.original} />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TradeDrilldown = ({ row }: { row: TopLiquidInsiderRow }) => (
    <div className="overflow-x-auto">
        <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-slate-400">
                <tr>
                    <th className="text-left py-1 pr-3">Filing</th>
                    <th className="text-left py-1 pr-3">Trade</th>
                    <th className="text-left py-1 pr-3">Insider</th>
                    <th className="text-left py-1 pr-3">Role</th>
                    <th className="text-right py-1 pr-3">Shares</th>
                    <th className="text-right py-1 pr-3">Price</th>
                    <th className="text-right py-1 pr-3">Value</th>
                    <th className="text-right py-1 pr-3">Owned After</th>
                    <th className="text-center py-1 pr-3">D/I</th>
                    <th className="text-center py-1">SEC</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/60 dark:divide-slate-700/60">
                {row.trades.map((t, i) => (
                    <tr key={`${t.url ?? ''}-${i}`}>
                        <td className="py-1.5 pr-3 font-mono">{formatDate(t.filing_date)}</td>
                        <td className="py-1.5 pr-3 font-mono">{formatDate(t.transaction_date)}</td>
                        <td className="py-1.5 pr-3 font-semibold text-slate-700 dark:text-slate-200 max-w-[200px] truncate">
                            {t.reporting_name ?? '—'}
                        </td>
                        <td
                            className="py-1.5 pr-3 text-slate-500 max-w-[200px] truncate"
                            title={t.type_of_owner ?? ''}
                        >
                            {t.type_of_owner ?? '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono">
                            {formatCompact(t.securities_transacted)}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono">{formatPrice(t.price)}</td>
                        <td className="py-1.5 pr-3 text-right font-mono font-semibold">
                            {formatCompactUsd(t.transaction_value)}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono">
                            {formatCompact(t.securities_owned)}
                        </td>
                        <td className="py-1.5 pr-3 text-center">{t.direct_or_indirect ?? '—'}</td>
                        <td className="py-1.5 text-center">
                            {t.url ? (
                                <a
                                    href={t.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex text-blue-600 dark:text-blue-400 hover:underline"
                                    aria-label="Open SEC filing"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            ) : (
                                '—'
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default InsiderTradesTable;
